/* ============================================================
   UK POSTCODE LOOKUP — shared, local, no third-party service

   Resolves a UK postcode, outward code or post town to a coordinate, and
   measures distance between coordinates. Used by both places we rank shops by
   distance: the Find in Store drawer on product pages, and the store locator on
   the Our Stores page. One implementation so the two can never disagree about
   where a postcode is or how far away a shop is.

   Data comes from assets/uk-outcodes.json — a centroid for every UK postcode
   district (2,856) and ~1,900 place names, about 42KB gzipped, fetched once per
   page and then held in memory.

   POSTCODES ARE RELIABLE; PLACE NAMES ARE BEST-EFFORT. Every UK postcode
   district is present, so a postcode always resolves. Place names come from the
   town and county columns of the same source and are patchy: cities and
   counties are well covered, but plenty of smaller towns (Ripon, Wilmslow,
   Altrincham, Devizes) are absent entirely. Callers should advertise the
   postcode as the way in, and treat an unresolved place name as normal rather
   than exceptional. Nothing is sent anywhere: the
   customer's postcode never leaves their browser, so there is no rate limit, no
   API key, no acceptable-use policy to stay inside, and nothing to disclose in
   the privacy policy.

   Accuracy: a district centroid is roughly a mile across, which is far finer
   than needed to rank shops that are tens of miles apart, but does not justify
   reporting a decimal. Use formatMiles() rather than printing a raw number.

   window.SWDPostcodeLookup
     .load(url)                  -> Promise, resolves when the data is ready
     .classify(text)             -> {kind: postcode|outcode|place, value} | null
     .resolve(text)              -> {lat, lng, label, kind} | null  (needs load)
     .distanceMiles(a, b, c, d)  -> straight-line miles
     .formatMiles(miles)         -> "12 miles" | "Less than a mile"
   ============================================================ */

window.SWDPostcodeLookup = (function () {
  var data = null;
  var pending = null;

  function load(url) {
    if (data) return Promise.resolve(data);
    if (!pending) {
      pending = fetch(url, { headers: { Accept: "application/json" } })
        .then(function (response) {
          if (!response.ok) throw new Error("Postcode data failed: " + response.status);
          return response.json();
        })
        .then(function (body) {
          data = body;
          return data;
        });
    }
    return pending;
  }

  function isLoaded() {
    return Boolean(data);
  }

  /* Anything that looks like a UK postcode — whole or part-typed — is a
     postcode. Only the outward code is ever used, so "MK40", "MK40 2" and
     "MK40 2QG" are all the same query and must all behave identically. The
     earlier version only accepted the complete postcode or the bare outward
     code, so a customer got results at "MK40" and then "no match" the moment
     they typed the next character.

     The pattern is the outward code followed by as much of the inward code as
     has been typed so far, if any. Anything else is a place name. */
  var POSTCODE_LIKE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d?[A-Z]{0,2}$/;

  function classify(raw) {
    var value = String(raw || "").trim().toUpperCase().replace(/\s+/g, " ");
    if (!value) return null;
    if (POSTCODE_LIKE.test(value)) {
      return { kind: "postcode", value: value };
    }
    return { kind: "place", value: value };
  }

  /* Outward codes to try, best guess first. Which prefix is the outward code is
     genuinely ambiguous from syntax alone — "TN11" is both a real outward code
     and the first four characters of "TN1 1YT" — so candidates are checked
     against the real data and the first one that exists wins.

       "MK40 2"    -> space says the outward code is MK40
       "MK40 2QG"  -> same
       "MK402QG"   -> complete, so the inward code is the last three characters
       "TN11"      -> no space, not complete: TN11 is itself an outward code */
  function outwardCandidates(value) {
    var out = [];
    var spaced = value.indexOf(" ");
    if (spaced > 0) out.push(value.slice(0, spaced));

    var compact = value.replace(/\s+/g, "");
    if (compact.length >= 5) out.push(compact.slice(0, -3));
    out.push(compact.slice(0, 4), compact.slice(0, 3), compact.slice(0, 2));

    return out.filter(function (candidate, i) {
      return candidate.length >= 2 && out.indexOf(candidate) === i;
    });
  }

  /* What to show the customer. A complete postcode is echoed back properly
     spaced ("TN11YT" -> "TN1 1YT"); a part-typed one shows just the outward
     code, since that is all that was actually used. */
  function displayPostcode(value, outward) {
    var compact = value.replace(/\s+/g, "");
    var inward = compact.slice(outward.length);
    return /^\d[A-Z]{2}$/.test(inward) ? outward + " " + inward : outward;
  }

  // "TN11YT" -> "TN1 1YT". The inward code is always the last three characters.
  function formatPostcode(compact) {
    return compact.slice(0, -3) + " " + compact.slice(-3);
  }

  function outwardOf(compact) {
    return compact.slice(0, -3);
  }

  function titleCase(value) {
    return value.toLowerCase().replace(/(^|[\s-])(\w)/g, function (m, sep, ch) {
      return sep + ch.toUpperCase();
    });
  }

  function resolve(raw) {
    if (!data) return null;
    var parsed = classify(raw);
    if (!parsed) return null;

    var outcodes = data.outcodes || {};
    var places = data.places || data.towns || {};
    var point;
    var label;

    if (parsed.kind === "postcode") {
      var candidates = outwardCandidates(parsed.value);
      for (var i = 0; i < candidates.length; i++) {
        if (outcodes[candidates[i]]) {
          point = outcodes[candidates[i]];
          label = displayPostcode(parsed.value, candidates[i]);
          break;
        }
      }
    } else {
      var key = parsed.value.toLowerCase();
      point = places[key];
      if (!point) {
        /* The data often holds the wider name — "Cambridgeshire" for Cambridge,
           "Cheshire East" for Knutsford. Fall back to the shortest key that
           begins with what was typed. Exact matches always win first, so this
           cannot hijack a real place name. */
        var prefixed = Object.keys(places).filter(function (name) {
          return name.indexOf(key) === 0;
        }).sort(function (a, b) { return a.length - b.length; });
        if (prefixed.length) {
          point = places[prefixed[0]];
        }
      }
      label = titleCase(parsed.value);
    }

    if (!point) return null;
    return { lat: point[0], lng: point[1], label: label, kind: parsed.kind };
  }

  function distanceMiles(lat1, lng1, lat2, lng2) {
    var R = 3958.8;
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  function distanceKm(lat1, lng1, lat2, lng2) {
    return distanceMiles(lat1, lng1, lat2, lng2) * 1.609344;
  }

  /* Whole numbers, and never a decimal: the origin is a district centroid about
     a mile across, so "0.4 miles" would be precision we do not have. */
  function formatMiles(miles) {
    if (miles < 1) return "Less than a mile";
    return Math.round(miles) + (Math.round(miles) === 1 ? " mile" : " miles");
  }

  return {
    load: load,
    isLoaded: isLoaded,
    classify: classify,
    resolve: resolve,
    formatPostcode: formatPostcode,
    outwardOf: outwardOf,
    outwardCandidates: outwardCandidates,
    distanceMiles: distanceMiles,
    distanceKm: distanceKm,
    formatMiles: formatMiles,
  };
})();
